import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Axios instance for the app. Auth is transported via httpOnly session cookie
 * (server sets `access_token` on `/api/auth/login`). Every request must include
 * credentials so the browser attaches the cookie.
 *
 * There is no localStorage token — sessions are httpOnly cookies only.
 */
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fmtMoney = (n, currency) => {
  const cur = currency || localStorage.getItem("hvac_currency") || "INR";
  const locale = cur === "INR" ? "en-IN" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  } catch {
    return `${cur} ${Number(n || 0).toFixed(2)}`;
  }
};

export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

/**
 * Authenticated file download helper. Uses the session cookie via
 * `credentials: "include"` — no need to inject an Authorization header.
 * Returns a Blob so the caller can trigger a download or preview.
 */
export async function downloadBlob(path) {
  const res = await fetch(`${API}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  return res.blob();
}

/**
 * Trigger a file download with integrity verification + live progress.
 *
 * Streams the response body chunk-by-chunk via the fetch ReadableStream API.
 * Calls `onProgress({ loaded, total, pct })` on every chunk so callers can
 * update a toast in real time. Validates the response BEFORE saving:
 *   · non-2xx → parses the JSON error body and shows it as a toast (a direct
 *     <a download> link would silently save the error JSON as a broken file)
 *   · network truncation mid-transfer → the stream throws → error toast, no
 *     corrupt file saved
 * On success the blob is handed to the browser's native save dialog.
 *
 * Note: Content-Length may be missing when the server sends the response
 * chunked-transfer-encoded (large PDFs behind some proxies). In that case
 * we still show the bytes-received counter — just without a percentage.
 */
export async function triggerFileDownload(path, filename, onProgress) {
  const res = await fetch(`${API}${path}`, { credentials: "include" });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try {
      const j = await res.json();
      msg = formatApiErrorDetail(j?.detail) || msg;
    } catch { /* non-JSON error body */ }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const total = Number(res.headers.get("Content-Length")) || 0;
  const contentType = res.headers.get("Content-Type") || "application/octet-stream";

  let loaded = 0;
  const chunks = [];
  const reader = res.body?.getReader?.();

  if (reader) {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      if (onProgress) {
        onProgress({ loaded, total, pct: total ? Math.round((loaded / total) * 100) : null });
      }
    }
  } else {
    // Fallback for browsers without ReadableStream support
    chunks.push(new Uint8Array(await res.arrayBuffer()));
    loaded = chunks[0].byteLength;
    if (onProgress) onProgress({ loaded, total: loaded, pct: 100 });
  }

  const blob = new Blob(chunks, { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
  return { size: loaded, filename };
}

/**
 * Convenience wrapper: shows a sonner toast that live-updates with progress
 * (`Downloading foo.pdf · 42%`) and clears itself on completion or error.
 * Requires `sonner` — call sites just do:
 *   downloadWithToast('/quotations/x/pdf', 'x.pdf')
 */
export async function downloadWithToast(path, filename) {
  const { toast } = await import("sonner");
  const toastId = toast.loading(`Preparing ${filename}…`);
  try {
    await triggerFileDownload(path, filename, ({ loaded, total, pct }) => {
      const kb = (loaded / 1024).toFixed(0);
      const totalKb = total ? ` / ${(total / 1024).toFixed(0)} KB` : " KB";
      const suffix = pct != null ? ` · ${pct}%` : "";
      toast.loading(`Downloading ${filename} · ${kb}${totalKb}${suffix}`, { id: toastId });
    });
    toast.success(`Downloaded ${filename}`, { id: toastId, duration: 2500 });
  } catch (e) {
    toast.error(e.message || "Download failed", { id: toastId, duration: 5000 });
    throw e;
  }
}
